/**
 * FeatureUpgradePanel
 *
 * Animated upgrade prompt showing feature use-case cards.
 * Used by FeaturePageGate (full-page) and ReportsPage (inline tab).
 *
 * Shows a visually rich presentation of what the locked feature offers,
 * with auto-scrolling cards and clear CTAs to upgrade plan or add-on.
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Lock,
  Sparkles,
  ArrowRight,
  Crown,
  Zap,
  ChevronLeft,
  ChevronRight,
  Plus,
  Loader2,
} from "lucide-react";
import {
  Search,
  TrendingUp,
  CalendarClock,
  Target,
  MessageSquareText,
  DollarSign,
  BarChart3,
  PieChart,
  FileText,
  Brain,
  AlertTriangle,
  Lightbulb,
  LineChart,
  Bot,
  Workflow,
  Clock,
  Bell,
  Settings,
  Rocket,
  Shield,
  Users,
  Globe,
  Layers,
  Star,
  Phone,
  PhoneOutgoing,
  Radio,
  AudioLines,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { authFetch } from "@/utils/api";
import { toast } from "sonner";
import { PLAN_DEFAULTS } from "@touchworks/shared/features.js";

// ── Determine the minimum plan that includes a feature ──────
const PLAN_DISPLAY_NAMES = {
  starter: "Starter",
  professional: "Professional",
  enterprise: "Enterprise",
};

const PLAN_MONTHLY_PRICES = {
  starter: 297,
  professional: 697,
  enterprise: null, // custom pricing
};

const PLAN_ORDER = ["starter", "professional", "enterprise"];

function getMinimumPlanForFeature(featureKey) {
  for (const plan of PLAN_ORDER) {
    const defaults = PLAN_DEFAULTS[plan];
    if (!defaults) continue;
    const feature = defaults[featureKey];
    if (!feature) continue;
    if (
      feature.limitType === "boolean" &&
      (feature.limitValue === 1 || feature.limitValue === true)
    ) {
      return { slug: plan, name: PLAN_DISPLAY_NAMES[plan] };
    }
    if (feature.limitType === "number" && feature.limitValue > 0) {
      return { slug: plan, name: PLAN_DISPLAY_NAMES[plan] };
    }
    if (feature.limitType === "unlimited") {
      return { slug: plan, name: PLAN_DISPLAY_NAMES[plan] };
    }
  }
  return { slug: "enterprise", name: "Enterprise" };
}

// ── Get the feature's category for add-on ───────────────────
function getFeatureCategory(featureKey) {
  const dot = featureKey.indexOf(".");
  return dot > 0 ? featureKey.substring(0, dot) : featureKey;
}

// ── Use-case cards per feature key ──────────────────────────
const FEATURE_USE_CASES = {
  "sales.enabled": {
    title: "Sales Intelligence",
    subtitle:
      "Detecte oportunidades de venda automaticamente em suas conversas",
    lossMessage:
      "Sem este recurso, você perde em média 23 oportunidades de venda por mês que passam despercebidas nas conversas",
    gradient: "from-emerald-600/20 via-cyan-600/15 to-emerald-900/10",
    accentColor: "emerald",
    icon: DollarSign,
    addonCategory: "sales",
    addonLabel: "Sales Intelligence",
    cards: [
      {
        icon: Search,
        title: "Detecção de Palavras-chave",
        description:
          "Identifique automaticamente quando clientes mencionam interesse em compra, preço ou orçamento",
      },
      {
        icon: TrendingUp,
        title: "Insights em Tempo Real",
        description:
          "Receba alertas instantâneos quando uma oportunidade de venda é identificada na conversa",
      },
      {
        icon: CalendarClock,
        title: "Agendamento Inteligente",
        description:
          "Programe mensagens de follow-up automáticas para não perder nenhuma oportunidade",
      },
      {
        icon: Target,
        title: "Score de Oportunidade",
        description:
          "Classifique leads automaticamente com base no nível de interesse detectado",
      },
      {
        icon: MessageSquareText,
        title: "Templates de Venda",
        description:
          "Use templates pré-configurados para acelerar o processo de conversão",
      },
      {
        icon: Lightbulb,
        title: "Sugestões de Abordagem",
        description:
          "Receba recomendações de próximas ações para conduzir o lead até o fechamento",
      },
    ],
  },

  "crm.enabled": {
    title: "CRM / Kanban",
    subtitle:
      "Organize todas as suas oportunidades em um Kanban visual integrado ao atendimento",
    lossMessage:
      "Sem o Kanban, sua equipe perde visibilidade do funil e oportunidades param sem follow-up — em média 30% dos leads são esquecidos",
    gradient: "from-blue-600/20 via-indigo-600/15 to-blue-900/10",
    accentColor: "blue",
    icon: Layers,
    addonCategory: "crm",
    addonLabel: "CRM / Kanban",
    cards: [
      {
        icon: Layers,
        title: "Pipeline Visual",
        description:
          "Visualize todas as oportunidades em colunas Kanban com arrastar-e-soltar entre etapas",
      },
      {
        icon: Target,
        title: "Múltiplos Pipelines",
        description:
          "Crie pipelines separados por equipe, produto ou tipo de venda",
      },
      {
        icon: Users,
        title: "Conexão com Contatos",
        description:
          "Cada card é vinculado a um contato e suas conversas — histórico completo a um clique",
      },
      {
        icon: ArrowRight,
        title: "Provider à Sua Escolha",
        description:
          "Use o CRM nativo da plataforma ou conecte AdvBox, Projuris ou Pipedrive — a página é a mesma",
      },
      {
        icon: BarChart3,
        title: "Métricas de Funil",
        description:
          "Acompanhe valor por etapa, taxa de conversão e tempo médio em cada estágio",
      },
      {
        icon: DollarSign,
        title: "Won/Lost Tracking",
        description:
          "Registre o desfecho de cada negócio com motivos, valor fechado e categorias de perda",
      },
    ],
  },

  "reports.insights": {
    title: "IA Insights",
    subtitle: "Análises inteligentes geradas por IA para decisões estratégicas",
    lossMessage:
      "Sem IA Insights, sua equipe gasta até 5h por semana analisando dados manualmente — tempo que poderia ser investido em atendimento",
    gradient: "from-violet-600/20 via-purple-600/15 to-violet-900/10",
    accentColor: "violet",
    icon: Brain,
    addonCategory: "reports",
    addonLabel: "Pacote de Relatórios",
    cards: [
      {
        icon: Brain,
        title: "Resumo Executivo",
        description:
          "Relatório consolidado com as métricas mais importantes do período",
      },
      {
        icon: AlertTriangle,
        title: "Detecção de Anomalias",
        description:
          "Identifique padrões incomuns e problemas antes que se agravem",
      },
      {
        icon: TrendingUp,
        title: "Análise de Tendências",
        description:
          "Entenda a evolução dos seus indicadores e preveja comportamentos",
      },
      {
        icon: Lightbulb,
        title: "Recomendações Acionáveis",
        description:
          "Sugestões práticas baseadas nos dados para melhorar resultados",
      },
      {
        icon: LineChart,
        title: "Previsões Preditivas",
        description:
          "Use machine learning para prever volume de atendimento e demanda",
      },
      {
        icon: Target,
        title: "Correlações Ocultas",
        description:
          "Descubra relações entre métricas que impactam sua operação",
      },
    ],
  },

  "reports.advanced": {
    title: "Relatórios Avançados",
    subtitle: "Analytics detalhados para gestão profissional",
    lossMessage:
      "Empresas sem analytics avançados tomam decisões 3x mais lentas e perdem oportunidades de otimização na operação",
    gradient: "from-blue-600/20 via-indigo-600/15 to-blue-900/10",
    accentColor: "blue",
    icon: BarChart3,
    addonCategory: "reports",
    addonLabel: "Pacote de Relatórios",
    cards: [
      {
        icon: BarChart3,
        title: "Dashboard Completo",
        description:
          "Visualize todas as métricas em dashboards interativos e filtráveis",
      },
      {
        icon: PieChart,
        title: "Análise por Canal",
        description:
          "Compare performance entre WhatsApp, Instagram, Telegram e outros canais",
      },
      {
        icon: Users,
        title: "Performance de Agentes",
        description:
          "Métricas individuais de tempo, satisfação e eficiência por atendente",
      },
      {
        icon: FileText,
        title: "Exportação Profissional",
        description:
          "Exporte relatórios em CSV e PDF para apresentações e análises externas",
      },
    ],
  },

  "reports.agents": {
    title: "Relatório de Agentes",
    subtitle: "Métricas detalhadas de performance da sua equipe",
    lossMessage:
      "Sem métricas de agentes, você não identifica gargalos no atendimento — atendentes improdutivos custam até R$ 2.000/mês em ticket perdido",
    gradient: "from-blue-600/20 via-cyan-600/15 to-blue-900/10",
    accentColor: "blue",
    icon: Users,
    addonCategory: "reports",
    addonLabel: "Pacote de Relatórios",
    cards: [
      {
        icon: Users,
        title: "Ranking de Atendentes",
        description:
          "Compare a performance de cada atendente com métricas claras",
      },
      {
        icon: Clock,
        title: "Tempo de Resposta",
        description:
          "Monitore o tempo médio de resposta por atendente e horários",
      },
      {
        icon: Star,
        title: "Satisfação por Agente",
        description:
          "Veja o score de satisfação individual de cada membro da equipe",
      },
      {
        icon: TrendingUp,
        title: "Evolução Semanal",
        description:
          "Acompanhe a melhoria contínua da equipe ao longo do tempo",
      },
    ],
  },

  "reports.channels": {
    title: "Relatório de Canais",
    subtitle: "Análise comparativa entre canais de comunicação",
    lossMessage:
      "Você pode estar investindo no canal errado — sem análise comparativa, é impossível saber onde concentrar seus esforços",
    gradient: "from-cyan-600/20 via-teal-600/15 to-cyan-900/10",
    accentColor: "cyan",
    icon: Globe,
    addonCategory: "reports",
    addonLabel: "Pacote de Relatórios",
    cards: [
      {
        icon: Globe,
        title: "Visão Multi-Canal",
        description:
          "Compare volume e performance entre todos os canais conectados",
      },
      {
        icon: BarChart3,
        title: "Métricas por Canal",
        description: "Tempo de resposta, satisfação e resolução por canal",
      },
      {
        icon: TrendingUp,
        title: "Tendência de Uso",
        description:
          "Identifique quais canais estão crescendo ou perdendo relevância",
      },
    ],
  },

  "reports.satisfaction": {
    title: "NPS & Satisfação",
    subtitle: "Métricas de satisfação e experiência do cliente",
    lossMessage:
      "Clientes insatisfeitos saem silenciosamente — sem NPS/CSAT, você só descobre quando já é tarde demais para reverter",
    gradient: "from-amber-600/20 via-yellow-600/15 to-amber-900/10",
    accentColor: "amber",
    icon: Star,
    addonCategory: "reports",
    addonLabel: "Pacote de Relatórios",
    cards: [
      {
        icon: Star,
        title: "Score NPS / CSAT",
        description:
          "Acompanhe o Net Promoter Score e satisfação dos seus clientes",
      },
      {
        icon: MessageSquareText,
        title: "Feedback Qualitativo",
        description: "Análise dos comentários e feedbacks dos clientes",
      },
      {
        icon: TrendingUp,
        title: "Evolução da Satisfação",
        description: "Monitore a tendência de satisfação ao longo do tempo",
      },
    ],
  },

  "reports.sla": {
    title: "SLA & Compliance",
    subtitle: "Monitore o cumprimento dos seus acordos de nível de serviço",
    lossMessage:
      "Violações de SLA não detectadas geram multas contratuais e perda de confiança — 68% dos clientes trocam de fornecedor após 2 atrasos",
    gradient: "from-rose-600/20 via-pink-600/15 to-rose-900/10",
    accentColor: "rose",
    icon: Shield,
    addonCategory: "reports",
    addonLabel: "Pacote de Relatórios",
    cards: [
      {
        icon: Shield,
        title: "Compliance de SLA",
        description: "Percentual de atendimentos dentro e fora do SLA definido",
      },
      {
        icon: Clock,
        title: "Tempo de Resolução",
        description:
          "Análise detalhada do tempo para resolução por complexidade",
      },
      {
        icon: AlertTriangle,
        title: "Alertas de Violação",
        description: "Notificações automáticas quando SLAs estão em risco",
      },
    ],
  },

  "reports.custom": {
    title: "Relatórios Personalizados",
    subtitle: "Crie dashboards sob medida para sua operação",
    lossMessage:
      "Relatórios genéricos não mostram o que importa para seu negócio — você toma decisões baseadas em dados incompletos",
    gradient: "from-fuchsia-600/20 via-purple-600/15 to-fuchsia-900/10",
    accentColor: "fuchsia",
    icon: Layers,
    addonCategory: "reports",
    addonLabel: "Pacote de Relatórios",
    cards: [
      {
        icon: Layers,
        title: "Dashboards Customizáveis",
        description:
          "Monte painéis com as métricas que fazem sentido para seu negócio",
      },
      {
        icon: Settings,
        title: "Métricas Personalizadas",
        description: "Defina KPIs e indicadores específicos da sua operação",
      },
      {
        icon: Bell,
        title: "Alertas Customizados",
        description:
          "Configure notificações quando métricas atingirem limites definidos",
      },
    ],
  },

  "calling.whatsapp": {
    title: "WhatsApp Business Calling",
    subtitle:
      "Atenda e realize chamadas de voz pelo WhatsApp diretamente na plataforma",
    lossMessage:
      "Sem chamadas, você perde o canal de comunicação mais efetivo — ligações convertem 3x mais que mensagens de texto e resolvem problemas complexos em minutos",
    gradient: "from-emerald-600/20 via-green-600/15 to-emerald-900/10",
    accentColor: "emerald",
    icon: Phone,
    addonCategory: "calling",
    addonLabel: "WhatsApp Calling",
    images: [
      "/assets/feature_calling_init.webp",
      "/assets/exemple_calling_permission.webp",
    ],
    cards: [
      {
        icon: Bot,
        title: "Agente de Voz IA",
        description:
          "Atendimento automático por IA com voz natural — responde, qualifica e direciona chamadas 24/7",
      },
      {
        icon: Shield,
        title: "Permissões Inteligentes",
        description:
          "Solicite permissão ao cliente antes de ligar com templates aprovados pela Meta",
      },
      {
        icon: AudioLines,
        title: "Gravação e Transcrição",
        description:
          "Todas as chamadas gravadas e transcritas automaticamente com IA para auditoria e treinamento",
      },
      {
        icon: Radio,
        title: "Transcrição em Tempo Real",
        description:
          "Acompanhe a conversa ao vivo com transcrição instantânea durante a chamada",
      },
      {
        icon: Clock,
        title: "Horário Comercial",
        description:
          "Configure horários de atendimento por canal — chamadas fora do expediente são tratadas pela IA",
      },
      {
        icon: PhoneOutgoing,
        title: "Chamadas Ativas",
        description:
          "Realize ligações para clientes diretamente da plataforma com um clique",
      },
    ],
  },

  "calling.transcription": {
    title: "Transcrição e Análise de Chamadas",
    subtitle:
      "Transcreva automaticamente suas chamadas e obtenha análises de vendas com IA",
    lossMessage:
      "Sem transcrição, insights valiosos das chamadas se perdem — você perde oportunidades de coaching e melhoria que podem aumentar conversões em até 40%",
    gradient: "from-violet-600/20 via-purple-600/15 to-violet-900/10",
    accentColor: "violet",
    icon: AudioLines,
    addonCategory: "calling",
    addonLabel: "Transcrição e Análise",
    cards: [
      {
        icon: AudioLines,
        title: "Transcrição Automática",
        description:
          "Todas as chamadas transcritas automaticamente com IA de alta precisão",
      },
      {
        icon: TrendingUp,
        title: "Análise AIDA",
        description:
          "Avaliação automática de qualidade usando o modelo AIDA (Atenção, Interesse, Desejo, Ação)",
      },
      {
        icon: Brain,
        title: "Análise de Sentimento",
        description:
          "Detecte o sentimento do cliente durante a conversa — positivo, neutro ou negativo",
      },
      {
        icon: Lightbulb,
        title: "Sugestões de Melhoria",
        description:
          "Receba sugestões personalizadas de como melhorar o atendimento em cada chamada",
      },
      {
        icon: Target,
        title: "Score de Qualidade",
        description:
          "Nota geral de 0 a 10 para cada chamada com detalhamento por etapa do funil",
      },
      {
        icon: FileText,
        title: "Histórico Completo",
        description:
          "Acesse transcrições e análises a qualquer momento para auditoria e treinamento",
      },
    ],
  },

  "inbox.groups": {
    title: "Grupos Internos",
    subtitle:
      "Canais de comunicação interna estilo Slack para sua equipe colaborar em tempo real",
    lossMessage:
      "Sem grupos internos, sua equipe depende de WhatsApp pessoal e e-mails — gerando perda de contexto e retrabalho que custa até 8h semanais",
    gradient: "from-indigo-600/20 via-blue-600/15 to-indigo-900/10",
    accentColor: "indigo",
    icon: Users,
    addonCategory: "inbox",
    addonLabel: "Grupos Internos",
    cards: [
      {
        icon: Users,
        title: "Canais por Equipe",
        description:
          "Crie canais públicos ou privados para cada departamento, projeto ou tema",
      },
      {
        icon: MessageSquareText,
        title: "Mensagens Rich Text",
        description:
          "Envie mensagens formatadas com menções, emojis, links e arquivos de mídia",
      },
      {
        icon: Bell,
        title: "Menções e Notificações",
        description:
          "Mencione colegas com @nome e receba notificações em tempo real de mensagens importantes",
      },
      {
        icon: Search,
        title: "Busca em Conversas",
        description:
          "Encontre rapidamente qualquer mensagem, arquivo ou decisão tomada nos canais",
      },
      {
        icon: Shield,
        title: "Controle de Acesso",
        description:
          "Defina quem pode participar de cada canal com convites e permissões granulares",
      },
      {
        icon: Layers,
        title: "Organização Inteligente",
        description:
          "Fixe mensagens importantes, responda em threads e mantenha o contexto organizado",
      },
    ],
  },

  "rag.enabled": {
    title: "Base de Conhecimento",
    subtitle:
      "Treine seus agentes de IA com documentos da sua empresa para respostas precisas e contextuais",
    lossMessage:
      "Sem Base de Conhecimento, seus agentes de IA respondem de forma genérica — perdendo até 60% das chances de resolver dúvidas na primeira interação",
    gradient: "from-teal-600/20 via-emerald-600/15 to-teal-900/10",
    accentColor: "emerald",
    icon: Brain,
    addonCategory: "rag",
    addonLabel: "Base de Conhecimento",
    cards: [
      {
        icon: FileText,
        title: "Upload Multi-formato",
        description:
          "Importe PDFs, documentos Word, planilhas e páginas web para alimentar a base de conhecimento",
      },
      {
        icon: Search,
        title: "Busca Semântica",
        description:
          "Encontre informações relevantes usando busca inteligente com embeddings vetoriais — vai além de palavras-chave",
      },
      {
        icon: Brain,
        title: "Respostas Contextuais",
        description:
          "Agentes de IA consultam a base automaticamente para dar respostas precisas baseadas nos seus documentos",
      },
      {
        icon: Bot,
        title: "Treinamento Contínuo",
        description:
          "Adicione novos documentos a qualquer momento — o conhecimento do agente evolui junto com sua empresa",
      },
      {
        icon: Shield,
        title: "Controle por Agente",
        description:
          "Defina quais documentos cada agente de IA pode consultar, garantindo respostas especializadas por área",
      },
    ],
  },
};

// Default fallback for features without specific use cases
const DEFAULT_USE_CASE = {
  title: "Funcionalidade Premium",
  subtitle: "Desbloqueie recursos avançados para potencializar sua operação",
  lossMessage:
    "Sem este recurso, sua operação deixa de aproveitar ferramentas que economizam horas de trabalho por semana",
  gradient: "from-cyan-600/20 via-blue-600/15 to-cyan-900/10",
  accentColor: "cyan",
  icon: Rocket,
  cards: [
    {
      icon: Zap,
      title: "Automação Avançada",
      description:
        "Automatize processos complexos e economize horas de trabalho manual",
    },
    {
      icon: BarChart3,
      title: "Analytics Detalhados",
      description:
        "Obtenha insights profundos sobre sua operação com dados em tempo real",
    },
    {
      icon: Bot,
      title: "IA Integrada",
      description:
        "Use inteligência artificial para otimizar atendimento e vendas",
    },
    {
      icon: Shield,
      title: "Controle Total",
      description:
        "Gerencie permissões, SLAs e compliance com ferramentas profissionais",
    },
    {
      icon: Globe,
      title: "Multi-Canal",
      description:
        "Conecte todos os canais de comunicação em uma plataforma unificada",
    },
    {
      icon: Workflow,
      title: "Fluxos Avançados",
      description:
        "Crie fluxos de automação sofisticados com condições e ações complexas",
    },
  ],
};

// ── Accent color variants ───────────────────────────────────
const ACCENT_CLASSES = {
  emerald: {
    bg: "bg-emerald-500/15 dark:bg-emerald-500/10",
    border: "border-emerald-500/30 dark:border-emerald-500/20",
    text: "text-emerald-700 dark:text-emerald-400",
    ring: "ring-emerald-500/30",
    badge: "border-emerald-600 text-emerald-700 dark:text-emerald-400",
    btn: "bg-emerald-600 hover:bg-emerald-500 shadow-emerald-900/30",
    btnOutline:
      "border-emerald-600 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10",
  },
  violet: {
    bg: "bg-violet-500/15 dark:bg-violet-500/10",
    border: "border-violet-500/30 dark:border-violet-500/20",
    text: "text-violet-700 dark:text-violet-400",
    ring: "ring-violet-500/30",
    badge: "border-violet-600 text-violet-700 dark:text-violet-400",
    btn: "bg-violet-600 hover:bg-violet-500 shadow-violet-900/30",
    btnOutline:
      "border-violet-600 text-violet-700 dark:text-violet-400 hover:bg-violet-500/10",
  },
  blue: {
    bg: "bg-blue-500/15 dark:bg-blue-500/10",
    border: "border-blue-500/30 dark:border-blue-500/20",
    text: "text-blue-700 dark:text-blue-400",
    ring: "ring-blue-500/30",
    badge: "border-blue-600 text-blue-700 dark:text-blue-400",
    btn: "bg-blue-600 hover:bg-blue-500 shadow-blue-900/30",
    btnOutline:
      "border-blue-600 text-blue-700 dark:text-blue-400 hover:bg-blue-500/10",
  },
  cyan: {
    bg: "bg-cyan-500/15 dark:bg-cyan-500/10",
    border: "border-cyan-500/30 dark:border-cyan-500/20",
    text: "text-cyan-700 dark:text-cyan-400",
    ring: "ring-cyan-500/30",
    badge: "border-cyan-600 text-cyan-700 dark:text-cyan-400",
    btn: "bg-cyan-600 hover:bg-cyan-500 shadow-cyan-900/30",
    btnOutline:
      "border-cyan-600 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-500/10",
  },
  amber: {
    bg: "bg-amber-500/15 dark:bg-amber-500/10",
    border: "border-amber-500/30 dark:border-amber-500/20",
    text: "text-amber-700 dark:text-amber-400",
    ring: "ring-amber-500/30",
    badge: "border-amber-600 text-amber-700 dark:text-amber-400",
    btn: "bg-amber-600 hover:bg-amber-500 shadow-amber-900/30",
    btnOutline:
      "border-amber-600 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10",
  },
  rose: {
    bg: "bg-rose-500/15 dark:bg-rose-500/10",
    border: "border-rose-500/30 dark:border-rose-500/20",
    text: "text-rose-700 dark:text-rose-400",
    ring: "ring-rose-500/30",
    badge: "border-rose-600 text-rose-700 dark:text-rose-400",
    btn: "bg-rose-600 hover:bg-rose-500 shadow-rose-900/30",
    btnOutline:
      "border-rose-600 text-rose-700 dark:text-rose-400 hover:bg-rose-500/10",
  },
  indigo: {
    bg: "bg-indigo-500/15 dark:bg-indigo-500/10",
    border: "border-indigo-500/30 dark:border-indigo-500/20",
    text: "text-indigo-700 dark:text-indigo-400",
    ring: "ring-indigo-500/30",
    badge: "border-indigo-600 text-indigo-700 dark:text-indigo-400",
    btn: "bg-indigo-600 hover:bg-indigo-500 shadow-indigo-900/30",
    btnOutline:
      "border-indigo-600 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-500/10",
  },
  fuchsia: {
    bg: "bg-fuchsia-500/15 dark:bg-fuchsia-500/10",
    border: "border-fuchsia-500/30 dark:border-fuchsia-500/20",
    text: "text-fuchsia-700 dark:text-fuchsia-400",
    ring: "ring-fuchsia-500/30",
    badge: "border-fuchsia-600 text-fuchsia-700 dark:text-fuchsia-400",
    btn: "bg-fuchsia-600 hover:bg-fuchsia-500 shadow-fuchsia-900/30",
    btnOutline:
      "border-fuchsia-600 text-fuchsia-700 dark:text-fuchsia-400 hover:bg-fuchsia-500/10",
  },
};

/**
 * FeatureUpgradePanel
 *
 * @param {string} featureKey - Feature key for use-case lookup
 * @param {string} featureName - Optional display name override
 * @param {'full' | 'inline'} variant - 'full' for full-page, 'inline' for tab/section
 */
export default function FeatureUpgradePanel({
  featureKey,
  featureName,
  variant = "full",
}) {
  const config = FEATURE_USE_CASES[featureKey] || DEFAULT_USE_CASE;
  const accent = ACCENT_CLASSES[config.accentColor] || ACCENT_CLASSES.cyan;
  const Icon = config.icon;
  const navigate = useNavigate();
  const { isAdmin, isSuperAdmin, planSlug, refreshFeatures } = useAuth();

  const [activeCard, setActiveCard] = useState(0);
  const [activeImage, setActiveImage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [addonPrice, setAddonPrice] = useState(null);
  const [upgradePlanPrice, setUpgradePlanPrice] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const scrollRef = useRef(null);
  const cards = config.cards;
  const images = config.images || [];

  // Determine which plan includes this feature
  const requiredPlan = getMinimumPlanForFeature(featureKey);
  const addonLabel = config.addonLabel || config.title;

  // Fetch addon price — sempre por featureKey individual
  useEffect(() => {
    const params = `featureKey=${encodeURIComponent(featureKey)}`;
    authFetch(`/api/plans/account/addon-price?${params}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.totalPrice) setAddonPrice(data.totalPrice);
      })
      .catch(() => {});
  }, [featureKey]);

  // Fetch upgrade plan price (para dialog de confirmação)
  useEffect(() => {
    if (!requiredPlan.slug || requiredPlan.slug === planSlug) return;
    authFetch(
      `/api/plans/account/upgrade-price?planSlug=${encodeURIComponent(requiredPlan.slug)}`,
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.monthlyPrice) setUpgradePlanPrice(data.monthlyPrice);
      })
      .catch(() => {});
  }, [requiredPlan.slug, planSlug]);

  // Auto-rotate cards
  useEffect(() => {
    if (isPaused || cards.length <= 1) return;
    const interval = setInterval(() => {
      setActiveCard((prev) => (prev + 1) % cards.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isPaused, cards.length]);

  // Auto-rotate images
  useEffect(() => {
    if (isPaused || images.length <= 1) return;
    const interval = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isPaused, images.length]);

  // Scroll to active card
  useEffect(() => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const cardEl = container.children[activeCard];
      if (cardEl) {
        const scrollLeft =
          cardEl.offsetLeft -
          container.offsetWidth / 2 +
          cardEl.offsetWidth / 2;
        container.scrollTo({ left: scrollLeft, behavior: "smooth" });
      }
    }
  }, [activeCard]);

  const handlePrev = () => {
    setActiveCard((prev) => (prev - 1 + cards.length) % cards.length);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
  };

  const handleNext = () => {
    setActiveCard((prev) => (prev + 1) % cards.length);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
  };

  // Upgrade de plano — com confirmação
  const handleUpgrade = () => {
    if (isSuperAdmin()) {
      navigate("/internal/platform/plans");
      return;
    }
    setShowUpgradeDialog(true);
  };

  const confirmUpgrade = async () => {
    setShowUpgradeDialog(false);
    setIsUpgrading(true);
    try {
      const res = await authFetch("/api/plans/account/upgrade", {
        method: "POST",
        body: JSON.stringify({ planSlug: requiredPlan.slug }),
      });
      if (res.ok) {
        const data = await res.json();
        const priceMsg = data.monthlyPrice
          ? ` — R$ ${data.monthlyPrice.toFixed(2).replace(".", ",")}/mês`
          : "";
        toast.success(`Plano atualizado para ${data.planName}!${priceMsg}`);
        await refreshFeatures();
        window.location.reload();
      } else {
        const data = await res.json().catch(() => ({}));
        if (data.code === "NO_BILLING_CUSTOMER") {
          toast.error(
            data.message ||
              "Configure a integração de pagamento antes de trocar de plano.",
          );
        } else {
          toast.error(data.message || data.error || "Erro ao trocar de plano");
        }
      }
    } catch {
      toast.error("Erro ao trocar de plano");
    } finally {
      setIsUpgrading(false);
    }
  };

  // Contratar avulso (addon) — com confirmação
  const handleAddon = async () => {
    // Mostrar dialog de confirmação primeiro
    setShowConfirmDialog(true);
  };

  const confirmAddon = async () => {
    setShowConfirmDialog(false);
    setIsEnabling(true);
    try {
      const res = await authFetch("/api/plans/account/addon", {
        method: "POST",
        body: JSON.stringify({ featureKey }),
      });
      if (res.ok) {
        const data = await res.json();
        const monthlyMsg = data.billing?.newMonthlyValue
          ? ` — Novo valor mensal: R$ ${data.billing.newMonthlyValue.toFixed(2).replace(".", ",")}`
          : "";
        toast.success(`${addonLabel} ativado com sucesso!${monthlyMsg}`);
        // Refresh features in auth context
        await refreshFeatures();
        // Force page reload to update all components
        window.location.reload();
      } else {
        const data = await res.json().catch(() => ({}));
        if (res.status === 403) {
          toast.error(
            data.error || "Você não tem permissão para contratar este add-on.",
          );
        } else {
          toast.error(data.message || data.error || "Erro ao ativar add-on");
        }
      }
    } catch (err) {
      console.error("[FeatureUpgradePanel] Addon error:", err);
      if (err instanceof ApiError) {
        toast.error(
          err.data?.message ||
            err.data?.error ||
            err.message ||
            "Erro ao ativar add-on",
        );
      } else {
        toast.error("Erro ao ativar add-on");
      }
    } finally {
      setIsEnabling(false);
    }
  };

  const isFullPage = variant === "full";

  return (
    <div
      className={`flex flex-col items-center justify-center ${isFullPage ? "h-full min-h-[60vh]" : "min-h-[400px]"} relative overflow-hidden -mx-6 -mt-6 -mb-6 px-6 py-8`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background gradient — multi-layer for organic fill */}
      <div
        className={`absolute inset-0 bg-gradient-to-br ${config.gradient} pointer-events-none`}
      />
      <div
        className={`absolute inset-0 bg-gradient-to-tl ${config.gradient} opacity-50 pointer-events-none`}
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,255,255,0.03),transparent_60%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(255,255,255,0.02),transparent_60%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0),rgba(0,0,0,0.15)_100%)] pointer-events-none" />
      {/* Bottom fade — organic blend into page background */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background via-background/70 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-[radial-gradient(ellipse_at_bottom,rgba(10,10,10,0.9)_0%,transparent_70%)] pointer-events-none" />

      <div className="relative z-10 w-full space-y-8">
        {/* Header */}
        <div className="text-center space-y-4 animate-fade-in">
          {/* Icon + Badge */}
          <div className="flex justify-center">
            <div
              className={`relative w-20 h-20 rounded-2xl ${accent.bg} border ${accent.border} flex items-center justify-center animate-float`}
            >
              <Icon className={`w-10 h-10 ${accent.text}`} />
              <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-surface border border-border-strong flex items-center justify-center">
                <Lock className="w-3.5 h-3.5 text-foreground-muted" />
              </div>
            </div>
          </div>

          {/* Title */}
          <div>
            <Badge variant="outline" className={`${accent.badge} text-xs mb-3`}>
              <Crown className="w-3 h-3 mr-1" />
              Disponível no plano {requiredPlan.name}
            </Badge>
            <h2 className="text-2xl font-bold text-foreground-strong">
              {featureName || config.title}
            </h2>
            <p className="text-sm text-foreground-muted mt-2 max-w-lg mx-auto leading-relaxed">
              {config.subtitle}
            </p>
          </div>
        </div>

        {/* Image carousel (for features with screenshots) */}
        {images.length > 0 && (
          <div
            className="relative mx-auto animate-fade-in"
            style={{ maxWidth: "min(100%, 700px)", animationDelay: "150ms" }}
          >
            <div className="relative overflow-hidden rounded-2xl border border-border-strong/50 shadow-2xl bg-surface">
              {/* Image container with slide transition */}
              <div className="relative aspect-[16/9] overflow-hidden">
                {images.map((src, idx) => (
                  <img
                    key={idx}
                    src={src}
                    alt={`${config.title} — exemplo ${idx + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out ${
                      idx === activeImage
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-105"
                    }`}
                    loading={idx === 0 ? "eager" : "lazy"}
                  />
                ))}
                {/* Gradient overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-surface/80 to-transparent pointer-events-none" />
              </div>

              {/* Image dots */}
              {images.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setActiveImage(idx);
                        setIsPaused(true);
                        setTimeout(() => setIsPaused(false), 5000);
                      }}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        idx === activeImage
                          ? `w-6 ${accent.text.replace("text-", "bg-")}`
                          : "bg-white/40 hover:bg-white/60"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Image caption */}
            <div className="flex items-center justify-center gap-2 mt-3">
              <Sparkles className={`w-3.5 h-3.5 ${accent.text}`} />
              <span className="text-xs text-foreground-muted">
                {activeImage === 0
                  ? "Interface de chamadas integrada ao painel de atendimento"
                  : "Permissão de chamada solicitada ao cliente via template"}
              </span>
            </div>
          </div>
        )}

        {/* Cards carousel — single row, 3 visible + peek */}
        <div className="relative group">
          {/* Navigation arrows */}
          {cards.length > 3 && (
            <>
              <button
                onClick={handlePrev}
                className="absolute -left-1 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-surface-active/90 border border-border-strong flex items-center justify-center text-foreground-muted hover:text-foreground-strong hover:bg-border-strong transition-all opacity-0 group-hover:opacity-100"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={handleNext}
                className="absolute -right-1 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-surface-active/90 border border-border-strong flex items-center justify-center text-foreground-muted hover:text-foreground-strong hover:bg-border-strong transition-all opacity-0 group-hover:opacity-100"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}

          {/* Single-row horizontal scroll — shows 3 cards + 20% peek of next */}
          <div className="mx-auto" style={{ maxWidth: "min(100%, 820px)" }}>
            <div
              ref={scrollRef}
              className="flex gap-3 overflow-x-auto scroll-smooth px-2 no-scrollbar"
              style={{ scrollSnapType: "x mandatory" }}
            >
              {cards.map((card, idx) => {
                const CardIcon = card.icon;
                const isActive = idx === activeCard;
                return (
                  <div
                    key={idx}
                    onClick={() => {
                      setActiveCard(idx);
                      setIsPaused(true);
                      setTimeout(() => setIsPaused(false), 5000);
                    }}
                    className={`
                      flex-shrink-0 p-4 rounded-xl border cursor-pointer
                      transition-all duration-500 ease-out
                      ${
                        isActive
                          ? `${accent.bg} ${accent.border} ring-1 ${accent.ring} shadow-lg`
                          : "bg-surface/50 border-border hover:bg-surface-hover/50 opacity-70"
                      }
                    `}
                    style={{
                      width: "calc((100% - 1.5rem) / 3.2)",
                      scrollSnapAlign: "start",
                    }}
                  >
                    <div
                      className={`w-9 h-9 rounded-lg ${isActive ? accent.bg : "bg-surface-active"} flex items-center justify-center mb-3 transition-colors duration-300`}
                    >
                      <CardIcon
                        className={`w-5 h-5 ${isActive ? accent.text : "text-foreground-muted"} transition-colors duration-300`}
                      />
                    </div>
                    <h4
                      className={`text-sm font-semibold mb-1 transition-colors duration-300 ${isActive ? "text-foreground-strong" : "text-foreground-muted"}`}
                    >
                      {card.title}
                    </h4>
                    <p
                      className={`text-xs leading-relaxed transition-colors duration-300 ${isActive ? "text-foreground-secondary" : "text-foreground-muted"}`}
                    >
                      {card.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dots indicator */}
          <div className="flex justify-center gap-1.5 mt-4">
            {cards.map((_, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setActiveCard(idx);
                  setIsPaused(true);
                  setTimeout(() => setIsPaused(false), 5000);
                }}
                className={`
                  w-2.5 h-2.5 rounded-full transition-colors duration-300 border
                  ${
                    idx === activeCard
                      ? `${accent.text.replace("text-", "bg-")} ${accent.text.replace("text-", "border-")}`
                      : "bg-transparent border-border-strong hover:border-border"
                  }
                `}
              />
            ))}
          </div>
        </div>

        {/* Loss aversion message */}
        {config.lossMessage && (
          <div
            className="flex items-start gap-3 max-w-xl mx-auto px-4 py-3 rounded-xl bg-surface/60 border border-border animate-fade-in"
            style={{ animationDelay: "300ms" }}
          >
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-foreground-muted leading-relaxed">
              {config.lossMessage}
            </p>
          </div>
        )}

        {/* CTAs */}
        <div
          className="flex flex-col items-center gap-4 animate-fade-in"
          style={{ animationDelay: "400ms" }}
        >
          {/* Button row */}
          <div className="flex items-center gap-3 flex-wrap justify-center">
            {/* Trocar de Plano */}
            {isAdmin() && (
              <button
                onClick={handleUpgrade}
                disabled={isUpgrading}
                className={`flex items-center justify-center gap-2 px-6 py-3 ${accent.btn} text-white font-semibold rounded-xl transition-all shadow-lg group/btn disabled:opacity-50`}
              >
                {isUpgrading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Sparkles className="w-5 h-5" />
                )}
                Trocar para {requiredPlan.name}
                {upgradePlanPrice != null && (
                  <span className="text-xs opacity-75 ml-1">
                    R$ {upgradePlanPrice.toFixed(2).replace(".", ",")}/mês
                  </span>
                )}
                <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
              </button>
            )}

            {/* Contratar Avulso (addon) — only for admins */}
            {isAdmin() && (
              <button
                onClick={handleAddon}
                disabled={isEnabling}
                className={`flex items-center justify-center gap-2 px-6 py-3 border ${accent.btnOutline} font-semibold rounded-xl transition-all disabled:opacity-50`}
              >
                {isEnabling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Contratar {addonLabel}
                {addonPrice != null && (
                  <span className="ml-1 flex items-center gap-1.5 text-xs">
                    {(() => {
                      const planPrice = PLAN_MONTHLY_PRICES[requiredPlan.slug];
                      return planPrice && planPrice > addonPrice ? (
                        <span className="line-through opacity-40">
                          R$ {planPrice.toFixed(2).replace(".", ",")}
                        </span>
                      ) : null;
                    })()}
                    <span className="opacity-75">
                      R$ {addonPrice.toFixed(2).replace(".", ",")}/mês
                    </span>
                  </span>
                )}
              </button>
            )}
          </div>

          <p className="text-xs text-foreground-muted">
            {isAdmin()
              ? "Troque de plano ou contrate este recurso avulso no seu plano atual"
              : "Solicite ao administrador da conta para ativar este recurso"}
          </p>
        </div>
      </div>

      {/* Upgrade Plan Confirmation Dialog */}
      {showUpgradeDialog && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowUpgradeDialog(false)}
        >
          <div
            className="bg-surface border border-border-strong rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-5"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${accent.bg}`}>
                <Crown className={`w-5 h-5 ${accent.text}`} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground-strong">
                  Confirmar upgrade de plano
                </h3>
                <p className="text-sm text-foreground-muted">
                  {requiredPlan.name}
                </p>
              </div>
            </div>

            {/* Price breakdown */}
            <div className="space-y-3 p-4 rounded-xl bg-surface-active/60 border border-border-strong/50">
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground-secondary">
                  Plano {requiredPlan.name}
                </span>
                <span className="text-sm font-semibold text-foreground-strong">
                  {upgradePlanPrice != null
                    ? `R$ ${upgradePlanPrice.toFixed(2).replace(".", ",")}/mês`
                    : "Sob consulta"}
                </span>
              </div>
              {planSlug && (
                <div className="flex items-center justify-between text-xs text-foreground-muted pt-1 border-t border-border-strong/50">
                  <span>
                    Plano atual: {PLAN_DISPLAY_NAMES[planSlug] || planSlug}
                  </span>
                  <span>
                    {PLAN_MONTHLY_PRICES[planSlug]
                      ? `R$ ${PLAN_MONTHLY_PRICES[planSlug].toFixed(2).replace(".", ",")}/mês`
                      : ""}
                  </span>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex items-start gap-2.5 text-xs text-foreground-muted leading-relaxed">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p>
                Seu plano será atualizado imediatamente e o novo valor será
                cobrado no próximo ciclo. Todas as features do plano{" "}
                {requiredPlan.name} serão liberadas para sua conta.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={() => setShowUpgradeDialog(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-border-strong text-foreground-secondary text-sm font-medium hover:bg-surface-hover transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmUpgrade}
                disabled={isUpgrading}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl ${accent.btn} text-white text-sm font-semibold transition-all shadow-lg disabled:opacity-50`}
              >
                {isUpgrading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Confirmar upgrade
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Addon Confirmation Dialog */}
      {showConfirmDialog && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowConfirmDialog(false)}
        >
          <div
            className="bg-surface border border-border-strong rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-5"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${accent.bg}`}>
                <Icon className={`w-5 h-5 ${accent.text}`} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground-strong">
                  Confirmar contratação
                </h3>
                <p className="text-sm text-foreground-muted">{addonLabel}</p>
              </div>
            </div>

            {/* Price breakdown */}
            <div className="space-y-3 p-4 rounded-xl bg-surface-active/60 border border-border-strong/50">
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground-secondary">
                  Add-on {addonLabel}
                </span>
                <span className="text-sm font-semibold text-foreground-strong">
                  R${" "}
                  {addonPrice != null
                    ? addonPrice.toFixed(2).replace(".", ",")
                    : "--"}
                  /mês
                </span>
              </div>
              {(() => {
                const planPrice = PLAN_MONTHLY_PRICES[requiredPlan.slug];
                return planPrice && addonPrice && planPrice > addonPrice ? (
                  <div className="flex items-center justify-between text-xs text-foreground-muted pt-1 border-t border-border-strong/50">
                    <span>vs. trocar para {requiredPlan.name}</span>
                    <span className="line-through">
                      R$ {planPrice.toFixed(2).replace(".", ",")}/mês
                    </span>
                  </div>
                ) : null;
              })()}
            </div>

            {/* Info */}
            <div className="flex items-start gap-2.5 text-xs text-foreground-muted leading-relaxed">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p>
                O valor será adicionado à sua assinatura mensal atual. A
                cobrança será aplicada no próximo ciclo de faturamento. Você
                pode desativar este add-on a qualquer momento na página de
                billing.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-border-strong text-foreground-secondary text-sm font-medium hover:bg-surface-hover transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmAddon}
                disabled={isEnabling}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl ${accent.btn} text-white text-sm font-semibold transition-all shadow-lg disabled:opacity-50`}
              >
                {isEnabling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Confirmar contratação
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out both;
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}

/**
 * Export the USE_CASES map for external use
 */
export { FEATURE_USE_CASES, getMinimumPlanForFeature };
